import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faLink,
  faCircleNotch,
} from "@fortawesome/free-solid-svg-icons";
import PromotersList from "../components/Competitor/PromotersList";
import DocumentsList from "../components/Competitor/DocumentsList";
import Volume from "../components/Financial/Volume";
import HouseEdge from "../components/Competitor/HouseEdge";

function CompetitorPage() {
  const { competitorName } = useParams();
  const navigate = useNavigate();
  const decodedCompetitorName = decodeURIComponent(competitorName).replace(
    /-/g,
    " "
  );
  const [competitor, setCompetitor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCompetitor = async () => {
      try {
        console.log(
          `Fetching competitor details for: ${decodedCompetitorName}`
        );

        const token = localStorage.getItem("authToken");
        if (!token) {
          throw new Error("No authentication token found. Please log in.");
        }

        const response = await fetch("/.netlify/functions/fetchAll", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ name: decodedCompetitorName }),
        });

        console.log("Response Status:", response.status);

        if (response.status === 403) {
          console.error("Token expired or invalid, logging out...");
          localStorage.removeItem("authToken");
          localStorage.removeItem("authUser");
          window.location.href = "/login";
          return;
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || "Failed to fetch competitor details"
          );
        }

        const data = await response.json();
        console.log("Fetched Competitor Data:", data);

        // 🔹 Fix: Ensure case-insensitive lookup
        const foundCompetitor = data.find(
          (comp) =>
            comp.name.toLowerCase() === decodedCompetitorName.toLowerCase()
        );

        if (!foundCompetitor) {
          throw new Error("Competitor not found.");
        }

        setCompetitor(foundCompetitor);
      } catch (err) {
        console.error("Error fetching competitor:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompetitor();
  }, [decodedCompetitorName]);

  if (loading) {
    return (
      <div className="w-screen h-screen flex justify-center items-center bg-neutral-900">
        <FontAwesomeIcon
          icon={faCircleNotch}
          className="animate-spin text-4xl text-white"
        />
      </div>
    );
  }
  if (error) return <p className="text-red-500">Error: {error}</p>;
  if (!competitor) return <p>No competitor data found.</p>;

  return (
    <div className="relative flex flex-col items-center justify-center px-4 py-8 min-h-screen bg-neutral-900 text-white mx-auto">
      <button
        onClick={() => navigate("/")}
        className="absolute top-4 left-4 text-white text-lg flex items-center px-4 py-2 rounded-full hover:bg-neutral-800 transition duration-200"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
        Home
      </button>
      <div className="max-w-7xl w-full">
        <div className="flex flex-col items-center text-center mb-8">
          <img
            src={competitor.logo || "https://via.placeholder.com/150"}
            alt={`${competitor.name} logo`}
            className="w-32 h-32 rounded-full mb-2"
          />
          <h1 className="text-4xl font-bold">{competitor.name}</h1>
          <a
            href={competitor.link || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline mt-2 flex items-center"
          >
            <FontAwesomeIcon icon={faLink} className="mr-2" />
            Visit Website
          </a>
        </div>

        <div className="w-full bg-neutral-800 p-6 rounded-lg shadow-lg mb-8">
          <h2 className="text-2xl font-bold mb-4">Overview</h2>
          <p>{competitor.overview || "No description available"}</p>
        </div>

        {competitor.casino_tracked && <Volume competitor={competitor} />}

        {competitor?.id && (
          <div className="w-full bg-neutral-800 p-6 rounded-lg shadow-lg mb-8">
            <h2 className="text-2xl font-bold mb-4">Promoters</h2>
            <PromotersList competitorName={competitor.name} />
          </div>
        )}

        <div className="flex flex-row w-full">
          <div className="w-full bg-neutral-800 p-6 rounded-lg shadow-lg mb-8">
            <h2 className="text-2xl font-bold mb-4">House Edge</h2>
            <HouseEdge competitor={competitor} />
          </div>
        </div>

        <div className="w-full bg-neutral-800 p-6 rounded-lg shadow-lg mb-8">
          <h2 className="text-2xl font-bold mb-4">Documents</h2>
          <DocumentsList competitor={competitor} />
        </div>
      </div>
    </div>
  );
}

export default CompetitorPage;
